/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */

import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { NuqsAdapter } from "nuqs/adapters/react";

const IGNORED_SENTRY_ERROR_PATTERNS = [
  "[Cloudflare Turnstile] Error: 300030",
  "[Cloudflare Turnstile] Error: 600010",
  "[Cloudflare Turnstile] Nothing to reset found for provided container",
];

function isLocalDevelopmentHost() {
  const hostname = window.location.hostname;
  return hostname === "localhost" || hostname === "127.0.0.1";
}

function getSentryEventText(event: Sentry.Event, hint: Sentry.EventHint) {
  const parts: string[] = [];
  const originalException = hint.originalException;

  if (originalException instanceof Error) {
    parts.push(originalException.name, originalException.message);
  } else if (typeof originalException === "string") {
    parts.push(originalException);
  }

  if (event.message) parts.push(event.message);
  for (const exception of event.exception?.values ?? []) {
    if (exception.type) parts.push(exception.type);
    if (exception.value) parts.push(exception.value);
  }

  return parts.join("\n");
}

function shouldIgnoreSentryEvent(event: Sentry.Event, hint: Sentry.EventHint) {
  const eventText = getSentryEventText(event, hint);
  return IGNORED_SENTRY_ERROR_PATTERNS.some((pattern) => eventText.includes(pattern));
}

if (!isLocalDevelopmentHost()) {
  Sentry.init({
    dsn: "https://07d9a605e8e2d964993c384aab9c03ac@o4511081124921344.ingest.us.sentry.io/4511081126625281",
    sendDefaultPii: true,
    beforeSend(event, hint) {
      return shouldIgnoreSentryEvent(event, hint) ? null : event;
    },
  });
}

type RuntimeEnvPayload = Record<string, string>;

function normalizeRuntimeEnv(value: unknown): RuntimeEnvPayload | null {
  if (!value || typeof value !== "object") return null;
  const entries = Object.entries(value as Record<string, unknown>);
  const normalized: RuntimeEnvPayload = {};
  for (const [key, entry] of entries) {
    if (typeof entry !== "string") continue;
    const trimmed = entry.trim();
    if (trimmed.length > 0) normalized[key] = trimmed;
  }
  return normalized;
}

async function loadRuntimeEnv(): Promise<void> {
  try {
    const response = await fetch("/__env.json", {
      method: "GET",
      cache: "no-store",
      credentials: "same-origin",
    });
    if (!response.ok) return;
    const payload = normalizeRuntimeEnv(await response.json());
    if (!payload) return;
    globalThis.__APP_ENV__ = Object.freeze(payload);
  } catch {
    // Keep local default behavior in env.ts if runtime env fetch fails.
  }
}

async function bootstrap() {
  await loadRuntimeEnv();
  const [{ App }, { Providers }] = await Promise.all([
    import("./App"),
    import("@/components/Providers"),
  ]);

  const elem = document.getElementById("root");
  if (!elem) throw new Error("Missing #root element");

  const app = (
    <StrictMode>
      <Providers>
        <NuqsAdapter>
          <App />
        </NuqsAdapter>
      </Providers>
    </StrictMode>
  );

  if (import.meta.hot) {
    // With hot module reloading, `import.meta.hot.data` is persisted.
    const root = (import.meta.hot.data.root ??= createRoot(elem));
    root.render(app);
    return;
  }

  // The hot module reloading API is not available in production.
  createRoot(elem).render(app);
}

void bootstrap();
